// src/pages/logistics/PageRMCalc.jsx
import { useState, useMemo } from "react";

export function PageRMCalc({ rms, sfgs, sfgFormulas, fgs, fgFormulas }) {
  const [rows,   setRows]   = useState([{ fgId:"", qty:"" }]);
  const [hovCol, setHovCol] = useState(null);

  function addRow()         { setRows(r => [...r, { fgId:"", qty:"" }]); }
  function removeRow(i)     { setRows(r => r.filter((_,j) => j !== i)); }
  function updateRow(i,k,v) { setRows(r => r.map((x,j) => j===i ? {...x, [k]:v} : x)); }

  const totals = useMemo(() => {
    const map = {};
    for (const { fgId, qty } of rows) {
      const q = parseFloat(qty) || 0;
      if (!fgId || !q) continue;
      for (const slot of (fgFormulas[fgId] || [])) {
        const prop   = slot.proportion ?? slot.qty ?? 0;
        const isSFG  = slot.type === "SFG" || slot.type === "sfg";
        const compId = slot.componentId ?? slot.id;
        if (isSFG) {
          for (const sf of (sfgFormulas[compId] || [])) {
            const rmId    = sf.rmId ?? sf.id;
            const sfgProp = sf.proportion ?? sf.qty ?? 0;
            map[rmId] = (map[rmId] || 0) + prop * sfgProp * q;
          }
        } else {
          map[compId] = (map[compId] || 0) + prop * q;
        }
      }
    }
    return Object.entries(map)
      .map(([id, qty]) => ({ id, name: rms.find(r => r.id===id)?.name || id, qty }))
      .sort((a,b) => b.qty - a.qty);
  }, [rows, fgFormulas, sfgFormulas, rms]);

  function exportCSV() {
    const fgLines = rows.filter(r => r.fgId && parseFloat(r.qty) > 0).map(r => `${r.fgId},${parseFloat(r.qty)}`);
    const rmLines = totals.map(t => `${t.id},${t.qty.toFixed(3)}`);
    const csv  = ["FG Code,Quantity (kg)", ...fgLines, "", "RM Code,Quantity (kg)", ...rmLines].join("\r\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `rm-requirements-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">RM Calculator</div>
          <div className="page-subtitle">Enter FGs and quantities to calculate total raw material requirements</div>
        </div>
      </div>
      <div className="card">
        <div style={{ padding:14 }}>
          {rows.map((row, i) => (
            <div key={i} style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center" }}>
              <select className="form-select" style={{ flex:1 }} value={row.fgId}
                onChange={e => updateRow(i, "fgId", e.target.value)}>
                <option value="">Select FG…</option>
                {fgs.map(f => <option key={f.id} value={f.id}>{f.id} — {f.name}</option>)}
              </select>
              <input className="form-input input-right" style={{ width:120 }} type="number"
                placeholder="Qty (kg)" value={row.qty} onChange={e => updateRow(i, "qty", e.target.value)} />
              <span className="text-muted" style={{ fontSize:11 }}>kg</span>
              <button className="btn-small" onClick={() => removeRow(i)}>✕</button>
            </div>
          ))}
          <button className="btn" style={{ fontSize:11 }} onClick={addRow}>+ Add FG</button>
        </div>
      </div>
      {totals.length > 0 && (
        <>
          <div className="action-bar">
            <span className="fw-700" style={{ fontSize:13 }}>Total RM Requirements</span>
            <button className="btn" style={{ marginLeft:"auto", fontSize:11 }} onClick={exportCSV}>↓ Export to Excel</button>
          </div>
          <div className="card">
            <div className="table-wrap">
            <table className="data-table" data-hov-col={hovCol ?? undefined} onMouseLeave={() => setHovCol(null)}>
              <thead><tr>
                <th>RM Code</th>
                <th>RM Name</th>
                <th style={{ textAlign:"right" }}>Quantity (kg)</th>
              </tr></thead>
              <tbody>
                {totals.map(t => (
                  <tr key={t.id}>
                    <td onMouseEnter={() => setHovCol(0)}><span className="badge badge-gray">{t.id}</span></td>
                    <td className="fw-600" onMouseEnter={() => setHovCol(1)}>{t.name}</td>
                    <td className="fw-700" style={{ textAlign:"right" }} onMouseEnter={() => setHovCol(2)}>{t.qty.toFixed(3)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
